import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  Space,
  Typography,
  Tag,
  Button,
  Input,
  Modal,
  Form,
  message,
  Spin,
  Alert,
  Descriptions,
  Row,
  Col,
  Statistic,
  Select
} from 'antd';
import {
  ArrowLeftOutlined,
  SearchOutlined,
  ReloadOutlined,
  PlayCircleOutlined,
  CheckCircleOutlined,
  LockOutlined,
  ApiOutlined,
  FilePdfOutlined
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import VMSInvoiceService from '../../../services/VMSInvoiceService';
import { classifyError, vmsErrorText } from './VMSInvoiceErrors';
import { getStatusColor, getStatusIcon, getStatusLabel, isNotProcessed } from './VMSInvoiceStatus';
import PrefixPath from '../../../config/AppConfig';
import Headers from '../../../redux/Actions/Headers/Header';
import './BatchDetail.scss';
import Utils from '../../../utils/Utils';
import ResponsiveGrid from 'Components/TableMui/ResponsiveGrid';
import ContentCard from 'Components/Card/Card';

// Component to handle PDF link - uses authenticated download
const PdfLink = ({ fileGuid, displayFileName }) => {
  const [downloading, setDownloading] = React.useState(false);
  const displayName = displayFileName || fileGuid;

  const handleDownload = async (e) => {
    e.preventDefault();
    try {
      setDownloading(true);
      const response = await VMSInvoiceService.downloadPdfByGuid(fileGuid);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch (error) {
      // The service interceptor already warned the user and is redirecting.
      if (classifyError(error) === 'auth') return;

      let errorMsg = 'Failed to download PDF file.';
      try {
        if (error.response?.data instanceof Blob) {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          if (json.message) errorMsg = json.message;
        } else if (error.response?.data?.message) {
          errorMsg = error.response.data.message;
        }
      } catch (_) {
        // keep default message
      }
      message.error(errorMsg);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <a
      href="#"
      onClick={handleDownload}
      title={fileGuid}
      style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', opacity: downloading ? 0.5 : 1 }}
    >
      <FilePdfOutlined spin={downloading} />
      {displayName}
    </a>
  );
};

PdfLink.propTypes = {
  fileGuid: PropTypes.string.isRequired,
  displayFileName: PropTypes.string
};

const { Text } = Typography;
const { Option } = Select;

const BatchDetail = () => {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [searchField, setSearchField] = useState('all');
  const [processModalVisible, setProcessModalVisible] = useState(false);
  const [currentInvoice, setCurrentInvoice] = useState(null);
  const [errorDetailInvoice, setErrorDetailInvoice] = useState(null);
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [form] = Form.useForm();

  useEffect(() => {
    if (batchId) {
      fetchBatchInvoices();
    }
  }, [batchId]);

  // Page header (title bar) shown by the app layout
  useEffect(() => {
    dispatch(Headers.setHeader({ title: `Batch #${batchId} - Invoices` }));
    return () => dispatch(Headers.resetHeader());
  }, [dispatch, batchId]);

  const fetchBatchInvoices = async () => {
    try {
      setLoading(true);
      const response = await VMSInvoiceService.getInvoicesByBatch(batchId);

      if (response.data.success) {
        setInvoices(response.data.invoices || []);
        setFilteredInvoices(response.data.invoices || []);
      }
      
      setLoading(false);
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.message;
      const reason = classifyError(error);
      if (reason === 'auth') {
        setLoading(false);
        return;
      }

      message.error(vmsErrorText(reason, {
        404: errorMsg || 'Batch not found. It may have been deleted.',
        500: errorMsg || 'An unexpected server error occurred. Please try again later.',
        unavailable: errorMsg || 'The server is currently unavailable. Please try again later.',
        fallback: errorMsg || 'Unable to load batch details. Please try again.'
      }));

      console.error('Fetch batch invoices error:', errorMsg);
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setCurrentPage(1);
    if (!value) {
      setFilteredInvoices(invoices);
      return;
    }

    const searchLower = value.toLowerCase();
    const filtered = invoices.filter(invoice => {
      if (searchField === 'all') {
        return Object.values(invoice).some(val =>
          String(val).toLowerCase().includes(searchLower)
        );
      }
      return String(invoice[searchField] || '').toLowerCase().includes(searchLower);
    });
    setFilteredInvoices(filtered);
  };

  const handleFieldChange = (field) => {
    setSearchField(field);
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      const filtered = invoices.filter(invoice => {
        if (field === 'all') {
          return Object.values(invoice).some(val =>
            String(val).toLowerCase().includes(searchLower)
          );
        }
        return String(invoice[field] || '').toLowerCase().includes(searchLower);
      });
      setFilteredInvoices(filtered);
      setCurrentPage(1);
    }
  };

  const handleClearFilters = () => {
    setSearchText('');
    setSearchField('all');
    setFilteredInvoices(invoices);
    setCurrentPage(1);
  };

  const handleProcessSingle = (invoice) => {
    setCurrentInvoice(invoice);
    setProcessModalVisible(true);
  };



  const handleProcessSubmit = async (values) => {
    try {
      setProcessing(true);
      
      const invoiceId = currentInvoice.id;
      const response = await VMSInvoiceService.processInvoice(invoiceId, {
        vendorPassword: values.vendorPassword,
        apiGuid: values.apiGuid
      });
      
      if (response.data.success) {
        message.success('Invoice processed successfully');
      }
      
      setProcessModalVisible(false);
      setCurrentInvoice(null);
      form.resetFields();
      
      // Refresh the list
      await fetchBatchInvoices();
      
      setProcessing(false);
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.message;
      const reason = classifyError(error);
      if (reason === 'auth') {
        setProcessing(false);
        return;
      }

      message.error(vmsErrorText(reason, {
        timeout: 'Processing timed out. Please check the batch status and try again if needed.',
        400: errorMsg || 'Invalid data. Please review the invoice information.',
        500: errorMsg || 'Server error during processing. Please try again later.',
        unavailable: errorMsg || 'The server is currently unavailable. Please try again later.',
        fallback: errorMsg || 'Unable to process invoices. Please try again.'
      }));

      console.error('Process invoices error:', errorMsg);
      setProcessing(false);
    }
  };

  const columns = [
    {
      field: 'invoiceNumber',
      label: 'Invoice #',
      width: 160,
      pinned: 'left',
      primary: true,
      sortable: false,
      render: (text) => <Text strong>{text}</Text>
    },
    {
      field: 'propertyId',
      label: 'Property ID',
      flex: 1.2,
      minWidth: 120,
      sortable: false
    },
    {
      field: 'vendorLocationKey',
      label: 'Vendor Location',
      flex: 1.5,
      minWidth: 150,
      sortable: false
    },
    {
      field: 'totalAmount',
      label: 'Amount',
      flex: 1.2,
      minWidth: 120,
      sortable: false,
      render: (amount) => `$${parseFloat(amount).toFixed(2)}`,
      exportValue: (amount) =>
        amount === null || amount === undefined || amount === '' ? '' : parseFloat(amount).toFixed(2)
    },
    {
      field: 'lineItemCount',
      label: 'Line Items',
      flex: 1.1,
      minWidth: 110,
      align: 'center',
      sortable: false,
      render: (count) => <Tag color="blue">{count}</Tag>
    },
    {
      field: 'invoiceDate',
      label: 'Invoice Date',
      flex: 1.3,
      minWidth: 130,
      sortable: false,
      render: (date) => Utils.formatDateUS(date),
      exportValue: (date) => Utils.formatDateUS(date)
    },
    {
      field: 'status',
      label: 'Status',
      flex: 1.3,
      minWidth: 130,
      sortable: false,
      render: (status) => (
        <Tag icon={getStatusIcon(status)} color={getStatusColor(status)}>
          {getStatusLabel(status)}
        </Tag>
      ),
      exportValue: (status) => getStatusLabel(status)
    },
    {
      field: 'errorMessage',
      label: 'Error',
      flex: 1.3,
      minWidth: 130,
      sortable: false,
      render: (error, record) => {
        const hasError = error || record.validationErrors;
        return hasError ? (
          <a onClick={() => setErrorDetailInvoice(record)}>Error Details</a>
        ) : '-';
      },
      exportValue: (error) => error || ''
    },
    {
      // Export-only column: hidden in the grid/cards (errors are shown in the
      // "Error Details" modal), but included in the CSV.
      field: 'validationErrors',
      label: 'Validation Errors',
      hideOnGrid: true,
      hideOnCard: true,
      exportValue: (ve) => {
        if (!ve) return '';
        return Array.isArray(ve)
          ? ve.map((v) => (typeof v === 'string' ? v : v.message || JSON.stringify(v))).join('; ')
          : String(ve);
      }
    },
    {
      field: 'fileGuid',
      label: 'PDF File',
      flex: 2,
      minWidth: 200,
      sortable: false,
      exportable: false,
      render: (fileGuid, record) => {
        if (!fileGuid) return '-';
        const rawName = record.FileName || record.fileName || record.pdfFileName || '';
        const displayName = rawName.split(/[/\\]/).pop() || rawName;
        return <PdfLink fileGuid={fileGuid} displayFileName={displayName} />;
      }
    },
    {
      field: 'actions',
      label: 'Actions',
      width: 140,
      pinned: 'right',
      sortable: false,
      render: (_, record) => (
        <Space>
          {(record.status?.toLowerCase() === 'pre' ||
            record.status?.toLowerCase() === 'failed' ||
            isNotProcessed(record.status)) && (
            <Button
              data-testid={`vmsinvoice-process-btn-${record.id}`}
              type="primary"
              size="small"
              icon={<PlayCircleOutlined />}
              onClick={() => handleProcessSingle(record)}
            >
              Process
            </Button>
          )}
          {record.status?.toLowerCase() === 'success' && (
            <Tag color="green" icon={<CheckCircleOutlined />}>Completed</Tag>
          )}
        </Space>
      )
    }
  ];



  const calculateStats = () => {
    const total = invoices.length;
    const pre = invoices.filter(i => i.status?.toLowerCase() === 'pre').length;
    const success = invoices.filter(i => i.status?.toLowerCase() === 'success').length;
    const failed = invoices.filter(i =>
      i.status?.toLowerCase() === 'failed' ||
      i.status?.toLowerCase() === 'validationfailed'
    ).length;
   
    const notProcessed = invoices.filter(i => isNotProcessed(i.status)).length;
    const totalAmount = invoices.reduce((sum, inv) => sum + parseFloat(inv.totalAmount || 0), 0);

    return { total, pre, success, failed, notProcessed, totalAmount };
  };

  const stats = calculateStats();

  // Client-side pagination fed through ResponsiveGrid's `server` prop so the
  // footer matches the batches list (antd-style pager) instead of the native
  // DataGrid footer. We slice the filtered rows locally per page.
  const totalInvoices = filteredInvoices.length;
  const lastPage = Math.max(1, Math.ceil(totalInvoices / pageSize));
  const safePage = Math.min(currentPage, lastPage);
  const pagedInvoices = filteredInvoices.slice((safePage - 1) * pageSize, safePage * pageSize);

  return (
    <div className="batch-detail">
      <ContentCard>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button
              data-testid="vmsinvoice-back-btn"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(`${PrefixPath.APP_PREFIX_PATH}/vms-invoice/batches`)}
            >
              Back to Batches
            </Button>
            <Button
              data-testid="vmsinvoice-detail-refresh-btn"
              icon={<ReloadOutlined />}
              onClick={fetchBatchInvoices}
              loading={loading}
            >
              Refresh
            </Button>
          </div>

          <Row gutter={[16, 16]}>
            <Col flex={1}>
              <Card>
                <Statistic
                  title="Total Invoices"
                  value={stats.total}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col flex={1}>
              <Card>
                <Statistic
                  title="Pending (Pre)"
                  value={stats.pre}
                  valueStyle={{ color: '#faad14' }}
                />
              </Card>
            </Col>
            <Col flex={1}>
              <Card>
                <Statistic
                  title="Successful"
                  value={stats.success}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col flex={1}>
              <Card>
                <Statistic
                  title="Not Processed"
                  value={stats.notProcessed}
                  valueStyle={{ color: '#8c8c8c' }}
                />
              </Card>
            </Col>
            <Col flex={1}>
              <Card>
                <Statistic
                  title="Failed"
                  value={stats.failed}
                  valueStyle={{ color: '#ff4d4f' }}
                />
              </Card>
            </Col>
          </Row>

          <Card title="Search Filters" size="small">
            <Row gutter={[16, 16]} align="middle">
              <Col span={6}>
                <Select
                  data-testid="vmsinvoice-search-field-select"
                  value={searchField}
                  onChange={handleFieldChange}
                  style={{ width: '100%' }}
                >
                  <Option value="all">All Fields</Option>
                  <Option value="invoiceNumber">Invoice #</Option>
                  <Option value="propertyId">Property ID</Option>
                  <Option value="vendorLocationKey">Vendor Location</Option>
                  <Option value="status">Status</Option>
                  <Option value="errorMessage">Error</Option>
                </Select>
              </Col>
              <Col span={12}>
                <Input
                  id="vmsinvoice-detail-search-input"
                  placeholder={searchField === 'all' ? 'Search all fields...' : `Search by ${searchField}...`}
                  prefix={<SearchOutlined />}
                  value={searchText}
                  onChange={(e) => handleSearch(e.target.value)}
                  allowClear
                />
              </Col>
              <Col span={6}>
                <Button data-testid="vmsinvoice-detail-clear-filters-btn" onClick={handleClearFilters}>
                  Clear Filters
                </Button>
              </Col>
            </Row>
          </Card>

          {stats.pre > 0 && (
            <Alert
              message="Pending Invoices"
              description={`You have ${stats.pre} invoice(s) with 'pre' status that need to be processed. Use the "Process" button on each invoice to process them individually.`}
              type="info"
              showIcon
              closable
            />
          )}

          {stats.notProcessed > 0 && (
            <Alert
              data-testid="vmsinvoice-not-processed-alert"
              message="Invoices Not Processed"
              description={`${stats.notProcessed} invoice(s) were never evaluated by the VMS system because a system error stopped the batch. They were not rejected - they are still valid and can be re-submitted in a new, smaller batch.`}
              type="info"
              showIcon
              closable
            />
          )}

          <Spin spinning={loading}>
            <ResponsiveGrid
              columns={columns.filter(col => col.field !== 'actions')}
              rows={pagedInvoices}
              getRowId={(row) => row.id}
              emptyText="No invoices found."
              server={{
                page: safePage,
                pageSize: pageSize,
                total: totalInvoices,
                onChange: (page, size) => {
                  if (size !== pageSize) {
                    setCurrentPage(1);
                    setPageSize(size);
                  } else {
                    setCurrentPage(page);
                  }
                }
              }}
              pageSizeOptions={[10, 20, 50, 100]}
              exportCsv={{
                fileName: `batch-${batchId}-invoices`,
                // Export every filtered invoice, not just the current page.
                getRows: () => filteredInvoices
              }}
            />
          </Spin>
        </Space>
      </ContentCard>

      <Modal
        data-testid="vmsinvoice-process-modal"
        title={`Process Invoice: ${currentInvoice?.invoiceNumber || ''}`}
        open={processModalVisible}
        onCancel={() => {
          setProcessModalVisible(false);
          setCurrentInvoice(null);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleProcessSubmit}
        >
          <Alert
            message="Vendor Credentials Required"
            description="Please provide vendor credentials to process the invoice(s) to VMS system."
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />

          <Form.Item
            label="Vendor Password"
            name="vendorPassword"
            rules={[{ required: true, message: 'Please enter vendor password' }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Enter vendor password"
              size="large"
            />
          </Form.Item>

          <Form.Item
            label="API GUID"
            name="apiGuid"
            rules={[
              { required: true, message: 'Please enter API GUID' },
              { 
                pattern: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
                message: 'Please enter a valid GUID format'
              }
            ]}
          >
            <Input
              prefix={<ApiOutlined />}
              placeholder="e.g., a1b2c3d4-e5f6-4a5b-8c9d-0e1f2a3b4c5d"
              size="large"
            />
          </Form.Item>

          {currentInvoice && (
            <Descriptions bordered size="small" column={1} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Invoice Number">{currentInvoice.invoiceNumber}</Descriptions.Item>
              <Descriptions.Item label="Amount">${parseFloat(currentInvoice.totalAmount).toFixed(2)}</Descriptions.Item>
              <Descriptions.Item label="Property ID">{currentInvoice.propertyId}</Descriptions.Item>
            </Descriptions>
          )}

          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button data-testid="vmsinvoice-process-cancel-btn" onClick={() => {
                setProcessModalVisible(false);
                setCurrentInvoice(null);
                form.resetFields();
              }}>
                Cancel
              </Button>
              <Button data-testid="vmsinvoice-process-submit-btn" type="primary" htmlType="submit" loading={processing}>
                Process Invoice
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        data-testid="vmsinvoice-error-detail-modal"
        title="Error Details"
        centered
        open={!!errorDetailInvoice}
        onOk={() => setErrorDetailInvoice(null)}
        onCancel={() => setErrorDetailInvoice(null)}
        cancelButtonProps={{ style: { display: 'none' } }}
        width={600}
        bodyStyle={{ maxHeight: '400px', overflowY: 'auto' }}
      >
        {errorDetailInvoice && (
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Error Message">
              {errorDetailInvoice.errorMessage ? (
                <Text type="danger">{errorDetailInvoice.errorMessage}</Text>
              ) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Validation Errors">
              {(() => {
                const ve = errorDetailInvoice.validationErrors;
                if (!ve) return '-';
                const lines = Array.isArray(ve)
                  ? ve.map(v => typeof v === 'string' ? v : v.message || JSON.stringify(v))
                  : [String(ve)];
                return (
                  <div>
                    {lines.map((line, i) => (
                      <Text key={i} type="danger" style={{ display: 'block' }}>{line}</Text>
                    ))}
                  </div>
                );
              })()}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default BatchDetail;
